/* eslint-disable no-console */
import { connect } from "react-redux";
import { useState, useEffect, useMemo, useCallback } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import CardTitle from "components/CardTitle";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTable from "components/DataTable";
import { useNavigate } from "react-router-dom";
import BookingSelector from "../amenitiesEvents/BookingSelector";
import serviceUtils from "../project/components/servicesConfig/serviceUtils";

const {
  getStatusColor,
  formatServiceEventsForTable,
  getActiveClientReservationIds,
  getClientReservationsById,
} = serviceUtils;

function CellDate({ value }) {
  const parsedValue = Number(value);
  return (
    <span>
      {Number.isFinite(parsedValue) ? moment(parsedValue).format("DD/MM/YYYY HH:mm") : "-"}
    </span>
  );
}

function CellStatus({ value }) {
  const { t } = useTranslation();

  const color = getStatusColor(value);

  return (
    <span
      style={{
        background: color,
        padding: "4px 10px",
        borderRadius: "12px",
        color: "white",
        fontSize: "12px",
        fontWeight: "bold",
        textTransform: "uppercase",
      }}
    >
      {t(value)}
    </span>
  );
}

function CellActive({ value }) {
  const isActive = Boolean(value);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <MDBox
        component="span"
        title={isActive ? "Active" : "Inactive"}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: isActive ? "#2ecc71" : "#7f8c8d",
        }}
      />
    </MDBox>
  );
}

const columns = ({ t }) => {
  const baseColumns = [
    { Header: t("active"), accessor: "active", Cell: CellActive, maxWidth: 20 },
    {
      Header: t("service"),
      accessor: "serviceName",
      maxWidth: 20,
    },
    {
      Header: t("apartment"),
      accessor: "apartmentName",
      maxWidth: 20,
    },
    {
      Header: t("client"),
      accessor: "clientName",
      maxWidth: 20,
    },
    {
      Header: t("project"),
      accessor: "projectName",
      maxWidth: 20,
    },
    {
      Header: t("state"),
      accessor: "status",
      Cell: CellStatus,
      maxWidth: 10,
    },
    {
      Header: t("units"),
      accessor: "serviceRequestNumber",
      width: 40,
    },
    {
      Header: t("isAssociated"),
      accessor: "isAssociatedLabel",
      maxWidth: 12,
    },
    {
      Header: t("unitCost"),
      accessor: "serviceUnitCostLabel",
      maxWidth: 16,
    },
    {
      Header: t("totalCost"),
      accessor: "totalCostLabel",
      maxWidth: 16,
    },
    {
      Header: t("startDate"),
      accessor: "start",
      Cell: CellDate,
      maxWidth: 10,
    },
    {
      Header: t("endDate"),
      accessor: "end",
      Cell: CellDate,
      maxWidth: 10,
    },
  ];

  return baseColumns;
};

function ServiceEvents({
  serviceEvents,
  services,
  projects,
  apartments,
  clientReservations,
  isProjectManager,
  userProjects,
}) {
  const [serviceEventsRows, setServiceEventsRows] = useState([]);
  const [openBookingSelector, setOpenBookingSelector] = useState(false);
  const [selectedStartDateTime, setSelectedStartDateTime] = useState(
    moment().format("YYYY-MM-DDTHH:mm")
  );
  const [selectedEndDateTime, setSelectedEndDateTime] = useState(
    moment().add(1, "week").format("YYYY-MM-DDTHH:mm")
  );
  const navigate = useNavigate();
  const { t } = useTranslation();

  const clientReservationsById = useMemo(
    () => getClientReservationsById(clientReservations),
    [clientReservations]
  );

  useEffect(() => {
    const formattedServiceEvents = formatServiceEventsForTable(
      serviceEvents,
      services,
      apartments,
      projects,
      clientReservationsById
    );

    setServiceEventsRows(formattedServiceEvents);
  }, [JSON.stringify(serviceEvents), projects, apartments, services, clientReservationsById]);

  const activeClientReservationIds = useMemo(
    () => getActiveClientReservationIds(clientReservations),
    [clientReservations]
  );

  const allEventsRows = useMemo(() => {
    const now = Date.now();

    return serviceEventsRows.map((serviceEventRow) => {
      const serviceEventEnd = Number(serviceEventRow?.end);

      return {
        ...serviceEventRow,
        active:
          activeClientReservationIds.has(serviceEventRow?.clientReservationId) &&
          Number.isFinite(serviceEventEnd) &&
          serviceEventEnd > now,
      };
    });
  }, [serviceEventsRows, activeClientReservationIds]);

  const selectedStartTimestamp = useMemo(
    () => (selectedStartDateTime ? moment(selectedStartDateTime).valueOf() : NaN),
    [selectedStartDateTime]
  );

  const selectedEndTimestamp = useMemo(
    () => (selectedEndDateTime ? moment(selectedEndDateTime).valueOf() : NaN),
    [selectedEndDateTime]
  );

  const filterEventsByDateRange = useCallback(
    (rows) =>
      (rows || []).filter((eventRow) => {
        const eventStart = Number(eventRow?.start);
        const eventEndCandidate = Number(eventRow?.end);
        const eventEnd = Number.isFinite(eventEndCandidate) ? eventEndCandidate : eventStart;

        if (!Number.isFinite(eventStart)) return false;

        if (Number.isFinite(selectedStartTimestamp) && eventEnd < selectedStartTimestamp) {
          return false;
        }

        if (Number.isFinite(selectedEndTimestamp) && eventStart > selectedEndTimestamp) {
          return false;
        }

        return true;
      }),
    [selectedEndTimestamp, selectedStartTimestamp]
  );

  const filteredAllEventsRows = useMemo(
    () => filterEventsByDateRange(allEventsRows),
    [allEventsRows, filterEventsByDateRange]
  );

  const tableColumns = useMemo(() => columns({ t }), [t]);

  const rowsToShow = filteredAllEventsRows;

  const rowsForDisplay = useMemo(
    () =>
      rowsToShow.map((row) => {
        const service = services?.[row?.serviceId] || {};
        const unitCost = Number(service?.cost) || 0;
        const units = Number(row?.serviceRequestNumber) || 0;
        const totalCost = unitCost * units;
        const currency = service?.currency || "";

        const formatCostValue = (value) => {
          if (!Number.isFinite(value)) return "0";
          const formatted = Number.isInteger(value) ? `${value}` : value.toFixed(2);
          return currency ? `${formatted} ${currency}` : formatted;
        };

        return {
          ...row,
          isAssociatedLabel: row?.isAssociated ? t("yes") : t("no"),
          clientName:
            clientReservationsById?.[row?.clientReservationId]?.displayName ||
            [
              clientReservationsById?.[row?.clientReservationId]?.firstName,
              clientReservationsById?.[row?.clientReservationId]?.lastName,
            ]
              .filter(Boolean)
              .join(" ") ||
            clientReservationsById?.[row?.clientReservationId]?.email ||
            "-",
          serviceUnitCostLabel: formatCostValue(unitCost),
          totalCostLabel: formatCostValue(totalCost),
        };
      }),
    [rowsToShow, clientReservationsById, services, t]
  );

  const titleToShow = t("serviceEvents");

  const add = () => {
    setOpenBookingSelector(true);
  };

  const handleBookingSelectorSubmit = ({ projectId, resourceId }) => {
    if (!projectId || !resourceId) return;

    setOpenBookingSelector(false);

    navigate(`/projects/${projectId}/service/${resourceId}`, {
      state: { initialDate: Date.now() },
    });
  };

  const resetDateRangeToThisWeek = () => {
    setSelectedStartDateTime(moment().format("YYYY-MM-DDTHH:mm"));
    setSelectedEndDateTime(moment().add(1, "week").format("YYYY-MM-DDTHH:mm"));
  };

  const sanitizeFilePart = (value = "") =>
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);

  const handleExportExcel = async () => {
    if (!rowsForDisplay?.length) return;

    const XLSX = await import("xlsx");
    const { saveAs } = await import("file-saver");

    const headers = [
      t("service"),
      t("apartment"),
      t("client"),
      t("project"),
      t("state"),
      t("unit"),
      t("isAssociated"),
      t("unitCost"),
      t("totalCost"),
      t("startDate"),
      t("endDate"),
    ];

    const rowsToExcel = rowsForDisplay.map((row) => {
      const start = Number(row?.start);
      const end = Number(row?.end);

      return {
        service: row?.serviceName || "",
        apartment: row?.apartmentName || "",
        client: row?.clientName || "-",
        project: row?.projectName || "",
        status: row?.status || "",
        services: row?.serviceRequestNumber ?? "",
        isAssociated: row?.isAssociated ? t("yes") : t("no"),
        serviceUnitCost: row?.serviceUnitCostLabel || "0",
        serviceTotalCost: row?.totalCostLabel || "0",
        start: Number.isFinite(start) ? moment(start).format("DD/MM/YYYY HH:mm") : "-",
        end: Number.isFinite(end) ? moment(end).format("DD/MM/YYYY HH:mm") : "-",
      };
    });

    const ws = XLSX.utils.json_to_sheet([], { header: headers });
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
    XLSX.utils.sheet_add_json(ws, rowsToExcel, { origin: "A2", skipHeader: true });

    ws["!cols"] = [
      { wch: 24 },
      { wch: 24 },
      { wch: 24 },
      { wch: 24 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const uniqueProjectNames = [
      ...new Set(rowsForDisplay.map((row) => row?.projectName).filter(Boolean)),
    ];
    const projectNameForFile =
      uniqueProjectNames.length === 1
        ? uniqueProjectNames[0]
        : `${uniqueProjectNames.length || t("multiple")}-projects`;

    const startDateForFile = selectedStartDateTime
      ? moment(selectedStartDateTime).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    const endDateForFile = selectedEndDateTime
      ? moment(selectedEndDateTime).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");

    const fileName = `service-events-${sanitizeFilePart(
      projectNameForFile
    )}-${startDateForFile}-${endDateForFile}.xlsx`;

    saveAs(blob, fileName);
  };

  return (
    <DashboardLayout>
      <Modal
        open={openBookingSelector}
        onClose={() => setOpenBookingSelector(false)}
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "38rem" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <BookingSelector
            featureKey="services"
            resourceCollectionKey="services"
            resourceLabel="service"
            projects={projects}
            resources={services}
            isProjectManager={isProjectManager}
            userProjects={userProjects}
            onClose={() => setOpenBookingSelector(false)}
            onSubmit={handleBookingSelectorSubmit}
          />
        </MDBox>
      </Modal>

      <DashboardNavbar title={t("serviceEvents")} route="/services" />

      <MDBox pt={6} pb={3}>
        <Card>
          <CardTitle title={titleToShow} icon="group" />

          <MDBox px={3} pt={2} pb={1} display="flex" gap={2} flexWrap="wrap">
            <MDBox width="300px">
              <MDInput
                fullWidth
                type="datetime-local"
                label={t("startDate")}
                value={selectedStartDateTime}
                onChange={(event) => setSelectedStartDateTime(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </MDBox>

            <MDBox width="300px">
              <MDInput
                fullWidth
                type="datetime-local"
                label={t("endDate")}
                value={selectedEndDateTime}
                onChange={(event) => setSelectedEndDateTime(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </MDBox>

            <MDBox display="flex" alignItems="center">
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={resetDateRangeToThisWeek}
              >
                {t("thisWeek")}
              </MDButton>
            </MDBox>
          </MDBox>

          <DataTable
            table={{
              columns: tableColumns,
              rows: rowsForDisplay,
            }}
            canSearch
            canActuate
            canAdd
            canCustomActuate
            canCustomActuateWhenNoSelection
            customAction={handleExportExcel}
            customActionIcon="download"
            customActionDisabled={!rowsToShow?.length}
            add={add}
            action={(row) => {
              const { serviceId, clientReservationId, start } = row?.original || {};
              const reservation = clientReservationsById?.[clientReservationId] || {};
              const resolvedProjectId = reservation?.projectId || "";
              const resolvedApartmentId = reservation?.apartmentId || "";

              if (!resolvedProjectId || !serviceId || !resolvedApartmentId) return;

              navigate(
                `/projects/${resolvedProjectId}/service/${serviceId}/apartmentId/${resolvedApartmentId}`,
                {
                  state: { initialDate: start },
                }
              );
            }}
          />
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

ServiceEvents.defaultProps = {
  serviceEvents: {},
  projects: {},
  apartments: {},
  services: {},
  clientReservations: [],
  isProjectManager: false,
  userProjects: {},
};

ServiceEvents.propTypes = {
  serviceEvents: PropTypes.object,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  services: PropTypes.object,
  clientReservations: PropTypes.array,
  isProjectManager: PropTypes.bool,
  userProjects: PropTypes.object,
};

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.any,
};

CellStatus.defaultProps = {
  value: "pending",
};
CellStatus.propTypes = {
  value: PropTypes.string,
};

CellActive.defaultProps = {
  value: false,
};
CellActive.propTypes = {
  value: PropTypes.any,
};

const mapStateToProps = (state) => {
  const {
    mainUserReducer = {},
    clientReservationsReducer = {},
    apartmentsReducer = {},
  } = state || {};

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};

  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};

  let clientReservations = Object.values(clientReservationsObj).flatMap((item) =>
    item && typeof item === "object" ? Object.values(item) : []
  );

  if (isProjectManager) {
    clientReservations = clientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
  }

  const serviceEventsReducer = state?.servicesReducer?.serviceEvents || {};
  const reservationsById = Object.values(clientReservationsObj).reduce(
    (accReservationsById, reservationsByClient) => ({
      ...accReservationsById,
      ...Object.values(reservationsByClient || {}).reduce(
        (accReservationItems, reservation) => ({
          ...accReservationItems,
          [reservation?.uid]: reservation,
        }),
        {}
      ),
    }),
    {}
  );

  const getAuthorizedProjectIdForEvent = (serviceEvent = {}, clientReservationId = "") => {
    const projectIdFromEvent = serviceEvent?.projectId;
    if (projectIdFromEvent) return projectIdFromEvent;

    const projectIdFromReservation = reservationsById?.[clientReservationId]?.projectId;
    if (projectIdFromReservation) return projectIdFromReservation;

    const apartmentId = serviceEvent?.apartmentId;
    const projectIdFromApartment = apartmentsReducer?.[apartmentId]?.projectId;
    if (projectIdFromApartment) return projectIdFromApartment;

    return "";
  };

  const isAuthorizedServiceEvent = (serviceEvent = {}, clientReservationId = "") => {
    const projectId = getAuthorizedProjectIdForEvent(serviceEvent, clientReservationId);

    return !!projectId && !!userProjects?.[projectId];
  };

  const filterEventsByReservation = (eventsById = {}, clientReservationId = "") =>
    Object.fromEntries(
      Object.entries(eventsById || {}).filter(([, serviceEvent]) =>
        isAuthorizedServiceEvent(serviceEvent, clientReservationId)
      )
    );

  const filterServiceEventsForProjectManager = (allServiceEvents = {}) =>
    Object.fromEntries(
      Object.entries(allServiceEvents || {})
        .map(([serviceId, eventsByClientReservation]) => {
          const filteredEventsByClientReservation = Object.fromEntries(
            Object.entries(eventsByClientReservation || {})
              .map(([clientReservationId, eventsById]) => [
                clientReservationId,
                filterEventsByReservation(eventsById, clientReservationId),
              ])
              .filter(([, filteredEventsById]) => Object.keys(filteredEventsById).length > 0)
          );

          return [serviceId, filteredEventsByClientReservation];
        })
        .filter(
          ([, filteredEventsByClientReservation]) =>
            Object.keys(filteredEventsByClientReservation).length > 0
        )
    );

  const serviceEvents = !isProjectManager
    ? serviceEventsReducer
    : filterServiceEventsForProjectManager(serviceEventsReducer);

  return {
    serviceEvents,
    services: state?.servicesReducer?.services,
    projects: state?.projectsReducer,
    apartments: state?.apartmentsReducer,
    clientReservations,
    isProjectManager,
    userProjects,
  };
};

export default connect(mapStateToProps)(ServiceEvents);
